import React, { useMemo } from "react";
import AreaAvatar from "../../components/Map/AreaAvatar";
import Helper from "../../Helper";
import { Area } from "../../models/Area";
import { Dropoff } from "../../models/Dropoff";
import { Equipment } from "../../models/Equipment";
import { Gate } from "../../models/Gate";
import { GateToDropoffRoute } from "../../models/GateToDropoffRoute";
import { HaulRoute } from "../../models/HaulRoute";
import { MapObject } from "../../models/MapObject";
import { CommandProjectInfo } from "../CommandTypes";

interface CommandMapLegendProps {
    project: CommandProjectInfo;
    data: {
        areas: {
            id: number;
            area_name: string;
        }[];
        dropoffs?: {
            id: number;
            name: string;
        }[];
        equipment: {
            id: number;
            equipment_name: string;
        }[];
        // Not every map view's backend service surfaces these - eg. the Deliveries Map only
        // includes a gate/haul route when one was actually selected on a delivery, and never
        // surfaces gate-to-dropoff routes at all.
        gates?: {
            id: number;
            name: string;
        }[];
        haul_routes?: {
            id: number;
            name: string;
        }[];
        gate_to_dropoff_routes?: {
            id: number;
            name: string;
        }[];
        map_objects: {
            id: number;
            project_id: number;
            mappable_type: string;
            mappable_id: number;
            uuid: string;
            geometry: "point" | "lineString" | "polygon";
            color: string;
            outline: string;
            opacity: number;
            icon: string;
            icon_anchor: string;
            icon_width: number;
            icon_units: string;
            coordinates: [number, number];
            created_at: string;
            updated_at: string;
            archived: boolean;
            radiuses: any[];
        }[];
    };
}

type LegendItem = {
    key: string;
    name: string;
    mappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute;
    mapObject: MapObject | null;
};

const CommandMapLegend: React.FC<CommandMapLegendProps> = ({ project, data }) => {
    if (!data || !data.areas || !data.equipment) {
        return null;
    }

    const mapObjects = useMemo(() => {
        return data.map_objects.map((obj) => MapObject.fromApiObject(obj, project.timezone));
    }, [data.map_objects]);

    const legendItems = useMemo((): LegendItem[] => {
        const mapObjectFor = (mappableType: string, mappableId: number) =>
            mapObjects.find((obj) => obj.mappableId === mappableId && obj.mappableType === mappableType) || null;
        const byName = (a: { name: string }, b: { name: string }) => (a.name || "").localeCompare(b.name || "");

        const groups: LegendItem[][] = [
            (data.dropoffs || []).map((dropoff) => ({
                key: `dropoff-${dropoff.id}`,
                name: dropoff.name,
                mappable: Dropoff.fromApiObject(dropoff, project.timezone),
                mapObject: mapObjectFor("Dropoff", dropoff.id),
            })),
            data.areas.map((area) => ({
                key: `area-${area.id}`,
                name: area.area_name,
                mappable: Area.fromApiObject(area, project.timezone),
                mapObject: mapObjectFor("Area", area.id),
            })),
            data.equipment.map((equipment) => ({
                key: `equipment-${equipment.id}`,
                name: equipment.equipment_name,
                mappable: Equipment.fromApiObject(equipment, project.timezone),
                mapObject: mapObjectFor("Equipment", equipment.id),
            })),
            (data.gates || []).map((gate) => ({
                key: `gate-${gate.id}`,
                name: gate.name,
                mappable: Gate.fromApiObject(gate, project.timezone),
                mapObject: mapObjectFor("Gate", gate.id),
            })),
            (data.haul_routes || []).map((haulRoute) => ({
                key: `haul-route-${haulRoute.id}`,
                name: haulRoute.name,
                mappable: HaulRoute.fromApiObject(haulRoute, project.timezone),
                mapObject: mapObjectFor("HaulRoute", haulRoute.id),
            })),
            (data.gate_to_dropoff_routes || []).map((gateToDropoffRoute) => ({
                key: `gate-to-dropoff-route-${gateToDropoffRoute.id}`,
                name: gateToDropoffRoute.name,
                mappable: GateToDropoffRoute.fromApiObject(gateToDropoffRoute, project.timezone),
                mapObject: mapObjectFor("GateToDropoffRoute", gateToDropoffRoute.id),
            })),
        ];

        return groups.flatMap((group) => group.sort(byName));
    }, [data, mapObjects, project.timezone]);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    React.useEffect(() => {
        const checkOverflow = () => {
            if (containerRef.current) {
                setIsOverflowing(containerRef.current.scrollHeight > containerRef.current.clientHeight);
            }
        };

        checkOverflow();
        window.addEventListener("resize", checkOverflow);
        return () => window.removeEventListener("resize", checkOverflow);
    }, [legendItems]);

    return (
        <div className="shrink-0 rounded-b-lg border border-t-0 p-4 pt-2 bg-gray-50 dark:bg-neutral-800 border-gray-200 dark:border-neutral-700">
            <div className="flex flex-row items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900 dark:text-plot-darkmode-white w-full">
                    Locations and Equipment
                </h4>
            </div>
            {legendItems.length == 0 && (
                <div className="w-full text-center italic">
                    No Locations or Equipment Found. Try adjusting your filters or search terms.
                </div>
            )}

            <div className="relative">
                <div
                    ref={containerRef}
                    className={Helper.classNames(
                        "flex flex-row gap-6 flex-wrap text-xs w-full text-gray-600 dark:text-neutral-300 transition-all duration-100 relative max-h-14 overflow-hidden"
                    )}
                >
                    {legendItems.map((item) => (
                        <div
                            key={item.key}
                            className="flex items-center gap-1 hover:cursor-pointer hover:scale-110 transition-all duration-200"
                        >
                            <AreaAvatar mappable={item.mappable} mapObject={item.mapObject} />
                            <span>{item.name}</span>
                        </div>
                    ))}
                </div>
                {isOverflowing && (
                    <div className="text-xs text-gray-500 dark:text-neutral-400 italic text-center mt-1">
                        More hidden
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommandMapLegend;
